import { icon, type IconName } from './icons';

// Section 41 fictional data — hero workflow card, animated as a live smart-routing run
export type HeroRoute = 'ai' | 'api' | 'rules' | 'agent' | 'human';

export interface HeroStep {
  readonly t: string;
  readonly s: string;
  readonly route: HeroRoute;
  readonly icon: IconName;
  readonly log: string;
  readonly human?: boolean;
}

/** Fictional data for the hero workflow card, animated as a live smart-routing run. */
export const HERO_STEPS: readonly HeroStep[] = [
  {
    t: 'Assessment Notice Received',
    s: 'Intake · notice classified (0.97)',
    route: 'ai',
    icon: 'file',
    log: 'Route selected → Document Intelligence · confidence 0.97',
  },
  {
    t: 'Parcel Matched',
    s: '74-219-083-014 · Cook County, IL',
    route: 'api',
    icon: 'search',
    log: 'API / system → parcel 74-219-083-014 matched',
  },
  {
    t: 'Filing Deadline Validated',
    s: 'Business rule · deadline {{DL_L}}',
    route: 'rules',
    icon: 'calendar',
    log: 'Business rule passed → filing deadline {{DL_S}}',
  },
  {
    t: 'Evidence Package',
    s: 'AI agent · assembling with your criteria',
    route: 'agent',
    icon: 'sparkle',
    log: 'AI agent → evidence package assembled · 14 exhibits',
  },
  {
    t: 'Professional Review',
    s: 'Assigned expert · Morgan Lee',
    route: 'human',
    icon: 'userCheck',
    log: 'Human checkpoint → Morgan Lee approved',
    human: true,
  },
  {
    t: 'Filing',
    s: 'Your workflow · after sign-off',
    route: 'api',
    icon: 'send',
    log: 'Workflow continues → filing submitted · case updated',
  },
];
const HERO_ROUTES: readonly (readonly [HeroRoute, string])[] = [
  ['rules', 'Rules'],
  ['ai', 'AI model'],
  ['agent', 'Agent'],
  ['api', 'API / System'],
  ['human', 'Expert'],
];
export function workflowPanel(): string {
  return `
  <div class="panel hero-panel" id="hero-panel" role="img" aria-label="Illustrative product UI: smart routing for case {{CASE}}, parcel 74-219-083-014 in Cook County, Illinois, tax year {{Y}}. Each step is routed to the right resource: notice intake to an AI model, parcel match to an API, deadline validation to a business rule, the evidence package to an AI agent, professional review to Morgan Lee as a human checkpoint, then filing. Next deadline {{DL_L}}.">
    <div aria-hidden="true">
      <div class="panel-head"><span><b>Case {{CASE}}</b> · Cook County, IL · Tax Year {{Y}}</span><span class="live" data-live><span class="dot run"></span><span data-stage>Evidence Review</span></span></div>
      <div class="hp-router">
        <span class="hp-rl">Smart routing</span>
        <div class="hp-routes">${HERO_ROUTES.map(([k, l]) => `<span data-route="${k}" class="${k === 'agent' ? 'on' : ''}">${l}</span>`).join('')}</div>
      </div>
      <ol class="steps hp-steps" style="--p:.6">
        ${HERO_STEPS.map((st, i) => {
          const init = i < 3 ? 'done' : i === 3 ? 'run' : i === 4 ? 'human' : 'queued';
          const chip = {
            done: '<span class="chip done">Complete</span>',
            run: '<span class="chip run">In Progress</span>',
            human: '<span class="chip human">Human Checkpoint</span>',
            queued: '<span class="chip queued">Queued</span>',
          }[init];
          return `<li class="step ${init}" data-i="${i}"><span class="node">${icon(init === 'done' ? 'check' : st.icon)}</span><div><div class="t">${st.t}</div><div class="sl">${st.s}</div></div>${chip}</li>`;
        }).join('')}
      </ol>
      <div class="hp-log"><span class="hp-time" data-time>09:15:08</span><span data-log>${HERO_STEPS[3].log}</span></div>
      <div class="kpis">
        <div class="kpi"><div class="l">Parcel ID</div><div class="v mono">74-219-083-014</div></div>
        <div class="kpi"><div class="l">Next deadline</div><div class="v">{{DL_S}}</div><div class="kd">{{DUE}}</div></div>
        <div class="kpi"><div class="l">Reviewer</div><div class="v">Morgan Lee</div></div>
      </div>
      <p class="cap">Illustrative product UI · fictional data</p>
    </div>
  </div>`;
}
